
import { useState } from 'react'
import { Navigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2 } from 'lucide-react'

export function Login() {
    const { signInWithEmail, user, loading } = useAuth()
    const { workspaces, loading: workspaceLoading } = useWorkspace()
    const [searchParams] = useSearchParams()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [emailLoading, setEmailLoading] = useState(false)

    // Get redirect parameter from URL (e.g., for invite acceptance)
    const redirectPath = searchParams.get('redirect')

    // Show loading while auth or workspace data is loading after login
    if (user && (loading || workspaceLoading)) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-white dark:bg-background">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-muted-foreground text-sm">Loading your workspace...</p>
                </div>
            </div>
        )
    }

    // If already logged in, redirect appropriately
    if (user && !loading && !workspaceLoading) {
        // If there's a redirect path (e.g., invite acceptance), use it
        if (redirectPath) {
            return <Navigate to={redirectPath} replace />
        }

        // If user has no workspaces, send to onboarding
        if (workspaces.length === 0) {
            return <Navigate to="/app/onboarding/setup" replace />
        }
        return <Navigate to="/app" replace />
    }

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setEmailLoading(true)

        try {
            await signInWithEmail(email, password)
            // Don't navigate immediately - let the component re-render
            // The redirect logic at the top will handle navigation once
            // both auth and workspace data are loaded
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign in')
            setEmailLoading(false)
        }
        // Keep emailLoading true after success to show loading state
        // until the redirect happens
    }

    return (
        <div className="h-screen overflow-hidden flex flex-col md:flex-row bg-white dark:bg-background">
            {/* Left side - Form */}
            <div className="w-full md:w-1/2 h-full flex flex-col justify-between p-8 md:p-16 lg:p-24 relative z-10">
                {/* Main Content */}
                <div className="max-w-md w-full mx-auto animate-fade-in">
                    {/* Logo (Inside main container for alignment) */}
                    <div className="flex items-center gap-2 mb-8 group cursor-pointer hover:opacity-90 transition-opacity">
                        <img 
                            src="/ReflowAI Logo.png" 
                            alt="ReflowAI" 
                            className="h-10 w-10 rounded-lg object-contain group-hover:scale-105 transition-transform"
                            width="50"
                            height="50"
                        />
                        <span className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">ReflowAI</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white leading-tight animate-slide-up stagger-1">
                        Welcome back!
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-10 leading-relaxed animate-slide-up stagger-2">
                        Sign in to continue tracking competitor pricing and staying ahead in the market.
                    </p>

                    {/* Email Form */}
                    <form className="space-y-4" onSubmit={handleEmailLogin}>
                        <div className="animate-slide-up stagger-3 space-y-4">
                            <div>
                                <label className="sr-only" htmlFor="email">Email address</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                                        <span className="material-icons text-[20px]">mail_outline</span>
                                    </div>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={emailLoading || loading}
                                        className="block w-full pl-11 pr-4 py-4 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm transition-all h-auto hover:border-gray-300 dark:hover:border-gray-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="sr-only" htmlFor="password">Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary transition-colors">
                                        <span className="material-icons text-[20px]">lock_outline</span>
                                    </div>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={emailLoading || loading}
                                        className="block w-full pl-11 pr-4 py-4 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary sm:text-sm transition-all h-auto hover:border-gray-300 dark:hover:border-gray-600"
                                    />
                                </div>
                            </div>
                        </div>

                        {error && (
                            <p className="text-sm text-red-500 text-center animate-fade-in">{error}</p>
                        )}

                        <Button
                            type="submit"
                            disabled={emailLoading || loading}
                            className="w-full flex justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/40 active:scale-[0.99] mt-2 h-auto"
                        >
                            {emailLoading ? (
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            ) : null}
                            Sign In
                        </Button>
                    </form>

                    <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400 animate-fade-in stagger-6">
                        Don't have an account? <Link className="text-primary hover:underline font-semibold" to={redirectPath ? `/app/onboarding/welcome?redirect=${encodeURIComponent(redirectPath)}` : "/app/onboarding/welcome"}>Sign up</Link>
                    </p>
                </div>

                {/* Footer */}
                <div className="mt-auto pt-6 mx-auto w-full max-w-md text-center">
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        By signing in, you agree to our <a className="underline hover:text-primary transition-colors" href="#">Privacy Policy</a> and <a className="underline hover:text-primary transition-colors" href="#">Terms of Service</a>.
                    </p>
                </div>
            </div>

            {/* Right side - Decorative Panel */}
            <div className="hidden md:flex md:w-1/2 bg-[#0F172A] relative overflow-hidden items-center justify-center p-12">
                {/* Background Gradients */}
                <div className="absolute top-[-10%] right-[-10%] w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none" />

                {/* Abstract Image Background - Fixed to fill height and width */}
                <div className="absolute inset-0 opacity-60 pointer-events-none">
                    <div className="w-full h-full bg-gradient-to-b from-transparent via-[#0F172A]/20 to-[#0F172A] absolute inset-0 z-10" />
                    <img
                        alt="Abstract 3D Blue Shape"
                        className="w-full h-full object-cover opacity-50 mix-blend-overlay"
                        src="/onboarding/abstract-blue.jpg"
                    />
                </div>

                {/* Feature Cards */}
                <div className="relative w-full max-w-lg z-10 flex flex-col gap-6">
                    {/* Real-time Tracking Card */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl shadow-2xl animate-float-slow self-end w-72 transform translate-x-4">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-300 border border-blue-500/30">
                                <span className="material-icons text-sm">trending_up</span>
                            </div>
                            <div>
                                <p className="text-white font-medium text-sm">Live Price Updates</p>
                                <p className="text-gray-300 text-xs mt-1">Track prices across 50+ stores in real-time.</p>
                            </div>
                        </div>
                        <div className="mt-3 h-1 w-full bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full w-4/5 bg-blue-500 rounded-full" />
                        </div>
                    </div>

                    {/* Analytics Card */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl shadow-2xl animate-float-medium self-start w-80 -ml-8">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 border border-emerald-500/30">
                                <span className="material-icons text-xs">analytics</span>
                            </div>
                            <span className="text-white font-medium text-sm">Weekly Insights</span>
                        </div>
                        <div className="flex justify-between items-center bg-black/20 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-gray-200 text-xs">Price changes detected</span>
                            </div>
                            <span className="text-xs text-emerald-400">+127</span>
                        </div>
                        <div className="flex justify-between items-center bg-black/20 rounded-lg p-2 mt-2">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                <span className="text-gray-200 text-xs">Competitor moves</span>
                            </div>
                            <span className="text-xs text-amber-400">24</span>
                        </div>
                    </div>

                    {/* Market Share Card */}
                    <div className="bg-white/90 text-gray-900 p-5 rounded-2xl shadow-2xl animate-float-fast self-center w-80 transform translate-y-4">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <p className="font-bold text-sm">Your Performance</p>
                                <p className="text-gray-500 text-xs">This month</p>
                            </div>
                            <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-1 rounded-full">Active</span>
                        </div>
                        <div className="flex items-end gap-1 h-16 w-full mt-2">
                            <div className="w-1/5 bg-gray-100 rounded-t-sm h-[40%]" />
                            <div className="w-1/5 bg-gray-200 rounded-t-sm h-[60%]" />
                            <div className="w-1/5 bg-gray-300 rounded-t-sm h-[75%]" />
                            <div className="w-1/5 bg-blue-500 rounded-t-sm h-[90%] relative group">
                                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                    Current
                                </div>
                            </div>
                            <div className="w-1/5 bg-gray-200 rounded-t-sm h-[50%]" />
                        </div>
                        <p className="text-xs text-gray-500 mt-3 border-t border-gray-100 pt-2">
                            Your dashboard is ready
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
